import React, { useEffect, useState } from "react";
import { api, API_BASE } from "../lib/api";
import { Handshake } from "lucide-react";
import { openSponsorForm } from "./SponsorInquiryModal";

const TIER_ORDER = ["Gold", "Silver", "Bronze"];
const TIER = {
  Gold:   { label: "GOLD",   color: "#F59E0B", h: "h-20 sm:h-24", size: "text-base", min: 160,
            card: { boxShadow: "0 0 12px 2px rgba(245, 158, 11, 0.4)", border: "1px solid rgba(245, 158, 11, 0.5)" } },
  Silver: { label: "SILVER", color: "#9CA3AF", h: "h-16 sm:h-20", size: "text-sm", min: 130,
            card: { boxShadow: "0 0 10px 2px rgba(156, 163, 175, 0.3)", border: "1px solid rgba(156, 163, 175, 0.4)" } },
  Bronze: { label: "BRONZE", color: "#CD7F32", h: "h-12 sm:h-16", size: "text-sm", min: 110,
            card: { boxShadow: "0 0 10px 2px rgba(205, 127, 50, 0.3)", border: "1px solid rgba(205, 127, 50, 0.4)" } },
};

const logoSrc = (s) => `${API_BASE}/sponsors/${s.id}/logo`;

const LogoCard = ({ s, tier }) => {
  const t = TIER[tier];
  const card = (
    <div className="flex items-center justify-center rounded-xl bg-white/[0.03] p-5 transition-transform hover:scale-[1.03]"
      style={{ ...t.card, minWidth: t.min, minHeight: t.min }}>
      <img src={logoSrc(s)} alt={s.company_name} title={s.company_name}
        className={`${t.h} w-auto object-contain max-w-[200px] opacity-90 hover:opacity-100 transition-opacity`} />
    </div>
  );
  return s.website_url ? (
    <a href={s.website_url} target="_blank" rel="noopener noreferrer" className="block" data-testid={`sponsor-logo-${s.id}`}>{card}</a>
  ) : (
    <div data-testid={`sponsor-logo-${s.id}`}>{card}</div>
  );
};

const groupByTier = (sponsors) => TIER_ORDER
  .map((tier) => ({ tier, items: sponsors.filter((s) => s.tier === tier) }))
  .filter((g) => g.items.length > 0);

const SponsorCtaBtn = () => (
  <button onClick={openSponsorForm} data-testid="become-sponsor-cta"
    className="inline-flex items-center gap-2 border border-brand text-brand hover:bg-brand hover:text-black transition-colors px-7 py-3.5 text-sm font-semibold uppercase tracking-wide">
    <Handshake className="w-4 h-4" /> Sponsor This Event →
  </button>
);

/** Landing-page sponsors block: logos by tier (glowing dark cards), or a "Partner With Us" CTA when empty. */
export function SponsorsSection() {
  const [sponsors, setSponsors] = useState(null);
  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/sponsors/public"); setSponsors(data.sponsors || []); }
      catch { setSponsors([]); }
    })();
  }, []);

  if (sponsors === null) return null;
  const groups = groupByTier(sponsors);
  const community = sponsors.filter((s) => s.tier === "Community");

  return (
    <section className="max-w-5xl mx-auto px-5 sm:px-8 py-6 md:py-8 lg:py-8 text-center" data-testid="sponsors-section">
      <div className="label-eyebrow mb-3">Partners</div>
      {groups.length > 0 ? (
        <>
          <h2 className="font-display text-4xl sm:text-5xl text-white leading-none">Our Sponsors</h2>
          <p className="text-sm text-gray-500 mt-3">Proudly supported by companies investing in innovation</p>
          <div className="mt-8 space-y-8" data-testid="sponsors-grid">
            {groups.map((g) => (
              <div key={g.tier}>
                <div className={`label-eyebrow mb-4 ${TIER[g.tier].size}`} style={{ color: TIER[g.tier].color }} data-testid={`sponsor-tier-${g.tier.toLowerCase()}`}>{TIER[g.tier].label}</div>
                <div className="flex flex-wrap items-center justify-center gap-6">
                  {g.items.map((s) => <LogoCard key={s.id} s={s} tier={g.tier} />)}
                </div>
              </div>
            ))}
          </div>
          {community.length > 0 && (
            <div className="mt-7 text-sm text-gray-400" data-testid="community-partners">
              <span className="text-gray-500">Community Partners: </span>{community.map((s) => s.company_name).join(" · ")}
            </div>
          )}
          <div className="mt-10"><SponsorCtaBtn /></div>
        </>
      ) : (
        <div className="mt-2 border border-brand/30 bg-brand/[0.04] p-9 sm:p-12 max-w-5xl mx-auto grid lg:grid-cols-12 gap-8 items-center text-left" data-testid="become-sponsor">
          <div className="lg:col-span-8">
            <h2 className="font-display text-4xl sm:text-5xl text-white leading-none">Sponsor This Event</h2>
            <p className="text-gray-400 mt-4 max-w-xl">Support IdeaThon and connect your organisation with student builders. Share your details to start a conversation.</p>
          </div>
          <div className="lg:col-span-4 lg:text-right"><SponsorCtaBtn /></div>
        </div>
      )}
    </section>
  );
}

/** Results-page acknowledgment: a compact "Supported by" logo row. Renders nothing when empty. */
export function SupportedBy({ sponsors }) {
  if (!sponsors || sponsors.length === 0) return null;
  return (
    <div className="max-w-5xl mx-auto px-5 sm:px-8 py-12 border-t border-white/5" data-testid="results-supported-by">
      <div className="label-eyebrow text-gray-500 text-center mb-6">Supported by</div>
      <div className="flex flex-wrap items-center justify-center gap-6">
        {groupByTier(sponsors).flatMap((g) => g.items.map((s) => (
          <LogoCard key={s.id} s={s} tier={g.tier} />
        )))}
      </div>
    </div>
  );
}
